<template>
  <div>
    <v-row>
      <!-- Exercise column -->
      <v-col cols="12">
        <exercise-logic-id
          exCollection="architectureExCollection"
          refCollection="propositionExCollection"
          @ready="onReady"
          @error="onError"
          @saved="onSaved"
          @savedNext="onSavedNext"
          @loadedEx="onLoadedEx"
          @modifyEx="onModifyEx"
          @loadedRef="onLoadedRef"
          :doc="doc"
          :shared="false"
          :projectShared="false"
          :pid="true"
          :eid="$route.params.eid"
          :synced="false"
        >
          <template slot-scope="{ saveExercise }">
            <v-stepper v-model="e1">
              <v-stepper-header>
                <v-stepper-step
                  color="amber"
                  :complete="e1 > 1"
                  step="1"
                  @click="el = 1"
                  >Audience</v-stepper-step
                >
                <v-divider></v-divider>
                <v-stepper-step
                  color="amber"
                  :complete="e1 > 2"
                  step="2"
                  @click="el = 2"
                  >Features / Benefits / Evidence</v-stepper-step
                >
                <v-divider></v-divider>
              </v-stepper-header>

              <v-stepper-items>
                <!-- Step 1 -->
                <v-stepper-content step="1">
                  <v-row>
                    <v-col cols="4" class="mb-4">
                      <h2
                        class="headline"
                        v-text="$t('exercise_architecture.step1.title')"
                      ></h2>
                    </v-col>

                    <v-col cols="8" class="" v-if="results.audience.length">
                      <p
                        v-for="(item, index) in $t(
                          'exercise_architecture.step1.description'
                        )"
                        :key="index"
                        v-text="item"
                      />
                    </v-col>
                  </v-row>

                  <v-expansion-panels light v-if="results.audience.length">
                    <v-expansion-panel>
                      <v-expansion-panel-header
                        ><span class="subtitle-1"
                          >Audiences <span class="small">(Expand)</span></span
                        ></v-expansion-panel-header
                      >
                      <v-expansion-panel-content>
                        <ul class="tags mt-5">
                          <li
                            :class="tag.color"
                            v-for="(tag, index) in results.audience"
                            :key="index"
                          >
                            {{ tag.title | sentence }}
                          </li>
                        </ul>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>

                  <v-row v-if="doc">
                    <v-col cols="12" offset="0">
                      <v-card light>
                        <v-card-title>Who is this for?</v-card-title>
                        <v-card-text>
                          <v-text-field
                            label="Name the audience"
                            v-model.trim="doc.title"
                            type="text"
                            placeholder="Type here"
                            id="title"
                            color="amber"
                            :readonly="!canSave"
                          ></v-text-field>
                        </v-card-text>
                      </v-card>

                      <v-row>
                        <v-col cols="4">
                          <v-card>
                            <v-card-text>
                              <unit-items
                                v-model="doc.audience.q1"
                                name="audience.q1"
                                title="1) What are the key messages you want audiences to take away?"
                                :addItem="canSave"
                              ></unit-items>
                            </v-card-text>
                          </v-card>
                        </v-col>
                        <v-col cols="4">
                          <v-card>
                            <v-card-text>
                              <unit-items
                                v-model="doc.audience.q2"
                                name="audience.q2"
                                title="2) What are they trying to achieve?"
                                :addItem="canSave"
                              ></unit-items> </v-card-text
                          ></v-card>
                        </v-col>
                        <v-col cols="4">
                          <v-card>
                            <v-card-text>
                              <unit-items
                                v-model="doc.audience.q3"
                                name="audience.q3"
                                title="3) What do you want these audiences to do?"
                                :addItem="canSave"
                              ></unit-items> </v-card-text
                          ></v-card>
                        </v-col>
                        <v-col cols="4">
                          <v-card>
                            <v-card-text>
                              <unit-items
                                v-model="doc.audience.q4"
                                name="audience.q4"
                                title="4) What concerns or questions might they have?"
                                :addItem="canSave"
                              ></unit-items> </v-card-text
                          ></v-card>
                        </v-col>
                        <v-col cols="4">
                          <v-card>
                            <v-card-text>
                              <unit-items
                                v-model="doc.audience.q5"
                                name="audience.q5"
                                title="5) Anything else?"
                                :addItem="canSave"
                              ></unit-items> </v-card-text
                          ></v-card>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <bottom-navigation
                    :save="canSave"
                    :next="true"
                    :prev="false"
                    :gridView="false"
                    :gridToggle="false"
                    :list="`architecture`"
                    @save="saveDoc(saveExercise, true)"
                    @prev="prevStep"
                    @next="canSave ? saveDoc(saveExercise, false) : nextStep()"
                  >
                  </bottom-navigation>
                </v-stepper-content>
                <!-- Step 2 -->
                <v-stepper-content step="2">
                  <v-row>
                    <v-col cols="4" class="mb-4">
                      <h2
                        class="headline"
                        v-text="$t('exercise_architecture.step2.title')"
                      ></h2>
                    </v-col>
                    <v-col cols="8" class="" v-if="results.features.length">
                      <p
                        v-for="(item, index) in $t(
                          'exercise_architecture.step2.description'
                        )"
                        :key="index"
                        v-text="item"
                      />
                    </v-col>
                  </v-row>

                  <v-expansion-panels
                    light
                    :multiple="true"
                    v-if="results.features.length"
                  >
                    <v-expansion-panel>
                      <v-expansion-panel-header
                        ><span class="subtitle-1"
                          >Features <span class="small">(Expand)</span></span
                        ></v-expansion-panel-header
                      >
                      <v-expansion-panel-content>
                        <ul class="tags mt-5">
                          <li
                            :class="tag.color"
                            v-for="(tag, index) in results.features"
                            :key="index"
                          >
                            {{ tag.title | sentence }}
                          </li>
                        </ul>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <v-expansion-panel>
                      <v-expansion-panel-header
                        ><span class="subtitle-1"
                          >Benefits <span class="small">(Expand)</span></span
                        ></v-expansion-panel-header
                      >
                      <v-expansion-panel-content>
                        <ul class="tags mt-5">
                          <li
                            :class="tag.color"
                            v-for="(tag, index) in results.benefits"
                            :key="index"
                          >
                            {{ tag.title | sentence }}
                          </li>
                        </ul>
                      </v-expansion-panel-content>
                    </v-expansion-panel>

                    <v-expansion-panel>
                      <v-expansion-panel-header
                        ><span class="subtitle-1"
                          >Evidence <span class="small">(Expand)</span></span
                        ></v-expansion-panel-header
                      >
                      <v-expansion-panel-content>
                        <ul class="tags mt-5">
                          <li
                            :class="tag.color"
                            v-for="(tag, index) in results.reasons"
                            :key="index"
                          >
                            {{ tag.title | sentence }}
                          </li>
                        </ul>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>

                  <v-row v-if="doc">
                    <v-col cols="12" offset="">
                      <v-row>
                        <v-col cols="4">
                          <v-card
                            ><v-card-text>
                              <unit-items
                                v-model="doc.content.q1"
                                name="content.q1"
                                title="1) What content can we provide to support this user?"
                                :addItem="canSave"
                              ></unit-items>
                            </v-card-text>
                          </v-card>
                        </v-col>
                        <v-col cols="4">
                          <v-card
                            ><v-card-text>
                              <unit-items
                                v-model="doc.content.q2"
                                name="content.q2"
                                title="2) How can you demonstrate the benefits of what you offer?"
                                :addItem="canSave"
                              ></unit-items></v-card-text
                          ></v-card>
                        </v-col>
                        <v-col cols="4">
                          <v-card
                            ><v-card-text>
                              <unit-items
                                v-model="doc.content.q3"
                                name="content.q3"
                                title="3) How do you want people to feel?"
                                :addItem="canSave"
                              ></unit-items> </v-card-text
                          ></v-card>
                        </v-col>
                        <v-col cols="4">
                          <v-card
                            ><v-card-text>
                              <unit-items
                                v-model="doc.content.q4"
                                name="content.q4"
                                title="4) What would communicate your difference?"
                                :addItem="canSave"
                              ></unit-items> </v-card-text
                          ></v-card>
                        </v-col>
                        <v-col cols="4">
                          <v-card
                            ><v-card-text>
                              <unit-items
                                v-model="doc.content.q5"
                                name="content.q5"
                                title="5) Anything else?"
                                :addItem="canSave"
                              ></unit-items> </v-card-text
                          ></v-card>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                  <bottom-navigation
                    :save="canSave"
                    :next="false"
                    :prev="true"
                    :list="`architecture`"
                    :gridView="false"
                    :gridToggle="false"
                    @save="saveDoc(saveExercise, true)"
                    @prev="prevStep"
                  >
                  </bottom-navigation>
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </template>
        </exercise-logic-id>
      </v-col>
    </v-row>

    <v-dialog v-model="dialog" max-width="800">
      <v-card v-if="dialogMode == 'save'">
        <v-card-title
          class="headline"
          v-text="$t('exercise_service.saved.title')"
        ></v-card-title>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="amber" text @click="closeDialog">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>

      <v-card v-if="dialogMode == 'add'">
        <v-card-title>Add note</v-card-title>

        <v-card-text>
          <activation-add @complete="closeDialog" />
        </v-card-text>
      </v-card>

      <v-card light v-if="dialogMode == 'show'">
        <v-card-text class="pa-10">
          <activation-show :id="cardId" @complete="closeDialog" />
        </v-card-text>
      </v-card>

      <v-card v-if="dialogMode == 'edit'">
        <v-card-title class="headline">Edit note</v-card-title>

        <v-card-text>
          <activation-edit :id="cardId" @complete="closeDialog" />
        </v-card-text>
      </v-card>

      <v-card light v-else>
        <v-card-title
          class="headline"
          v-text="$t('exercise_functionality.saved.title')"
        ></v-card-title>

        <v-card-actions>
          <v-btn
            color="white"
            top
            right
            absolute
            class="black--text"
            @click="dialog = false"
          >
            Close
          </v-btn>

          <v-btn
            class="grey lighten-3"
            :to="`/users/project/${this.$route.params.id}`"
          >
            Back to Project
          </v-btn>
          <v-btn
            class="amber"
            :to="`/users/project/${this.$route.params.id}/architecture`"
          >
            Back to List
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// @ is an alias to /src
import { mapState } from "vuex";
import { BottomNavigation } from "@/components/core";
import { ExerciseLogicId } from "@/components/exercises";

import { UnitItems } from "@/components/units";

const fb = require("@/firebaseConfig.js");

export default {
  name: "UserArchitectureId",
  data() {
    return {
      e1: 1,
      exercises: null,
      refs: null,
      dialog: false,
      dialogMode: "add",
      allItems: null,
      listName: "",
      canSave: true,
      assignedUser: null,
      initialLoadDone: false,
      isMerging: false,
      dirtyFields: new Set(),
      remoteDoc: null,
      results: {
        audience: [],
        features: [],
        benefits: [],
        reasons: [],
      },

      doc: {
        title: "",
        audience: {
          q1: [],
          q2: [],
          q3: [],
          q4: [],
          q5: [],
        },
        content: {
          q1: [],
          q2: [],
          q3: [],
          q4: [],
          q5: [],
        },
        list: {},
        listNames: [],
        allItems: [],
        audienceAll: [],
        contentAll: [],
      },
      listLength: 1,
      debug: false,
    };
  },
  components: {
    BottomNavigation,
    ExerciseLogicId,
    UnitItems,
  },
  watch: {
    e1(val) {
      if (val === 3) {
        this.createList();
      }
    },
    "doc.title"() {
      if (this.initialLoadDone && !this.isMerging) this.dirtyFields.add("title");
    },
    "doc.audience": {
      handler() {
        if (this.initialLoadDone && !this.isMerging) this.dirtyFields.add("audience");
      },
      deep: true,
    },
    "doc.content": {
      handler() {
        if (this.initialLoadDone && !this.isMerging) this.dirtyFields.add("content");
      },
      deep: true,
    },
  },
  computed: {
    ...mapState([
      "userProfile",
      "currentUser",
      "currentProject",
      "currentProjectModel",
    ]),
  },
  mounted() {
    this.checkPermission();
  },

  methods: {
    checkPermission() {
      const eid = this.$route.params.eid;
      if (!eid) return;
      Promise.all([
        fb.architectureExCollection.doc(eid).get(),
        fb.profilesCollection.where("uid", "==", this.currentUser.uid).get(),
      ]).then(([archDoc, profileSnapshot]) => {
        const isAdmin =
          !profileSnapshot.empty &&
          profileSnapshot.docs[0].data().webrole === "admin";
        if (archDoc.exists && archDoc.data().assignedUser) {
          this.assignedUser = archDoc.data().assignedUser;
        }
        if (isAdmin) {
          this.canSave = true;
        } else if (archDoc.exists && archDoc.data().assignedUser) {
          this.canSave = archDoc.data().assignedUser === this.currentUser.uid;
        }
      });
    },
    nextStep() {
      this.e1++;
    },
    prevStep() {
      this.e1--;
    },
    onReady() {},
    onError() {
      console.log("error");
    },
    onSaved(payload) {
      console.log("saved ex");
      this.restoreAssignedUser(payload);
      this.dialogMode = "";
      this.dialog = true;
    },
    restoreAssignedUser(payload) {
      // saveExercise uses ref.set() which overwrites the whole document and
      // drops the top-level assignedUser field, so re-apply it after save.
      if (!this.assignedUser || !payload || !payload.id) return;
      fb.architectureExCollection
        .doc(payload.id)
        .update({ assignedUser: this.assignedUser })
        .catch((error) => {
          console.log("Error restoring assignedUser", error);
        });
    },
    onLoadedRef(refs) {
      console.log("loaded refs", refs);
      refs.forEach((post) => {
        this.getResults(post.doc);
      });
    },
    onLoadedEx(items) {
      console.log("loaded exercises", items);
      if (!items) return;
      if (items[0].assignedUser) {
        this.assignedUser = items[0].assignedUser;
      }
      const remote = items[0].doc;
      this.remoteDoc = JSON.parse(JSON.stringify(remote));
      if (!this.initialLoadDone) {
        this.title = items[0].title;
        this.mergeArrays(remote);
        this.$nextTick(() => {
          this.initialLoadDone = true;
        });
      } else {
        this.mergeRemoteChanges(remote);
      }
    },
    onModifyEx(items) {
      // console.log("modify exercises", items);
      this.exercises = [...items];
    },
    onSavedNext(payload) {
      this.restoreAssignedUser(payload);
      this.nextStep();
    },
    addIdea() {
      this.dialogMode = "add";
      this.dialog = true;
    },
    getResults(doc) {
      this.pushToArray(doc.audience, "audience", "grey lighten-3");
      this.pushToArray(doc.benefits, "benefits", "grey lighten-3");
      this.pushToArray(doc.features, "features", "grey lighten-3");
      this.pushToArray(doc.reasons, "reasons", "grey lighten-3");
    },
    pushToArray(arr, model, color) {
      if (arr != undefined && typeof arr === Array) return;
      if (arr || arr.length > 0) {
        arr.forEach((y) => {
          this.results[model].push({ title: y, color: color });
        });
      }
    },
    addList() {
      // let l = Object.keys(this.doc.list).length + 1;
      let lc = this.listName.replace(/\s/g, "").toLowerCase();
      this.doc.listNames.push({ title: this.listName, name: lc });
      this.listName = "";
      this.$set(this.doc.list, lc, []);
    },
    onRemoveList(name) {
      // loop through elements in list and re-distribute to master list
      let items = [];
      Object.values(this.doc.list[name]).forEach((x) => {
        items.push(x);
      });
      this.doc.allItems = [...this.doc.allItems, ...items];
      let item = this.doc.listNames.find((x) => x.name === name);
      let index = this.doc.listNames.indexOf(item);
      this.doc.listNames.splice(index, 1);
      this.$delete(this.doc.list, name);
    },
    onEditList(params) {
      let item = this.doc.listNames.find((x) => x.name === params.oldName);
      let index = this.doc.listNames.indexOf(item);
      console.log(index);
      let lc = params.newTitle.replace(/\s/g, "").toLowerCase();
      let renamedItem = {
        title: params.newTitle,
        name: lc,
      };
      this.$set(this.doc.listNames, index, renamedItem);

      let obj = this.doc.list[params.oldName];
      delete this.doc.list[params.oldName];
      this.$set(this.doc.list, lc, obj);
    },
    clone(obj) {
      Object.assign({}, obj);
    },
    renameKey(object, key, newKey) {
      const clonedObj = this.clone(object);
      const targetKey = clonedObj[key];
      delete clonedObj[key];
      clonedObj[newKey] = targetKey;
      return clonedObj;
    },
    mergeRemoteChanges(remote) {
      this.isMerging = true;
      Object.entries(remote).forEach(([key, value]) => {
        if (!this.dirtyFields.has(key)) {
          if (typeof value !== "object") {
            this.doc[key] = value;
          } else {
            Object.entries(value).forEach(([subKey, subValue]) => {
              this.$set(this.doc[key], subKey, subValue);
            });
          }
        }
      });
      this.$nextTick(() => {
        this.isMerging = false;
      });
    },
    saveDoc(saveExercise, notify) {
      this.isMerging = true;
      if (this.remoteDoc) {
        Object.entries(this.remoteDoc).forEach(([key, value]) => {
          if (!this.dirtyFields.has(key)) {
            if (typeof value !== "object") {
              this.doc[key] = value;
            } else {
              Object.entries(value).forEach(([subKey, subValue]) => {
                this.$set(this.doc[key], subKey, subValue);
              });
            }
          }
        });
      }
      this.dirtyFields = new Set();
      this.$nextTick(() => {
        this.isMerging = false;
      });
      saveExercise(notify);
    },
    mergeArrays(arr) {
      Object.entries(arr).forEach(([outerKey, value]) => {
        if (typeof value !== "object") {
          this.doc[outerKey] = value;
        } else {
          Object.entries(value).forEach(([key, value]) => {
            this.doc[outerKey][key] = value;
          });
        }
      });
    },
    addNote() {
      this.dialogMode = "add";
      this.dialog = true;
    },
    editNote($event) {
      console.log("edit note", $event);
      this.dialogMode = "edit";
      this.cardId = $event;
      this.dialog = true;
    },
    showNote($event) {
      console.log("show note", $event);
      this.dialogMode = "show";
      this.cardId = $event;
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.dialogMode = "";
    },
    createList() {
      let items = [];
      Object.values(this.doc.audience).forEach((value) => {
        value.forEach((x) => {
          x.color = "blue lighten-4";
          items.push(x);
        });
      });
      Object.values(this.doc.content).forEach((value) => {
        value.forEach((x) => {
          x.color = "lime accent-1";
          items.push(x);
        });
      });
      let itemsFromList = [];
      Object.values(this.doc.list).forEach((value) => {
        value.forEach((x) => {
          itemsFromList.push(x);
        });
      });
      let all = [];
      items.forEach((x) => {
        if (!itemsFromList.some((y) => y.title == x.title)) {
          all.push(x);
        }
      });
      this.doc.allItems = all;
    },
  },
};
</script>

<style lang="scss"></style>
